import { FC, useState, useEffect } from 'react';
import { TextField } from 'ui/extensions/TextField';

interface IInputTextProps {
	/** @deprecated Solo por compatibilidad con MUI: el design system ignora estos props. */
	size?: 'small' | 'medium';
	sx?: object;
	fullWidth?: boolean;
	fontSize?: string;
	pattern?: RegExp;
	value?: string;
	handleChange?: CallableFunction;
	name?: string;
	id?: string;
	type?: string;
	label?: string;
	required?: boolean;
	disabled?: boolean;
	hint?: string;
	error?: string;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	maxLength?: number;
	onChange?(val: unknown): void;
	onKeydown?(event: React.KeyboardEvent<HTMLInputElement>): void;
	regexValidation?: RegExp;
	messageFormatError?: string;
}

/**
 * Input con estado interno: `handleChange` se llama con el texto ya aceptado, `regexValidation` marca
 * el formato inválido sin bloquear el tipeo. Es la API histórica; por dentro usa `TextField` del design system.
 */
export const FormGenericInput: FC<IInputTextProps> = ({
	value,
	handleChange = () => void 0,
	label,
	required,
	name = 'generic',
	id = 'generic',
	type = 'text',
	disabled = false,
	hint,
	error,
	startAdornment,
	endAdornment,
	maxLength,
	onChange,
	onKeydown,
	regexValidation,
	messageFormatError
}) => {
	const [internalState, setInternalState] = useState('');
	const [valueHasChanged, setValueHasChanged] = useState<number>(0);
	const [isValid, setIsValid] = useState<boolean>(true);

	const validateInput = (inputValue: string | undefined): boolean => {
		if (!regexValidation || inputValue === '' || inputValue === undefined) {
			return true;
		}
		return regexValidation.test(inputValue);
	};

	useEffect(() => {
		const valid = validateInput(value);
		setIsValid(valid);
		valid && setInternalState(value !== undefined ? value : '');
	}, [value]);

	useEffect(() => {
		if (valueHasChanged) {
			handleChange(internalState);
		}
	}, [valueHasChanged]);

	const formatError = !isValid ? (messageFormatError ?? 'No cumple con el formato') : undefined;

	return (
		<TextField
			id={id}
			type={type}
			name={name}
			label={label}
			disabled={disabled}
			required={required}
			value={internalState}
			maxLength={maxLength}
			hint={hint}
			error={error ?? formatError}
			startAdornment={startAdornment}
			endAdornment={endAdornment}
			onKeyDown={(ev) => {
				onKeydown?.(ev);
				if (ev.key.includes('Arrow')) return;
				if (maxLength && internalState.length >= maxLength && ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey) {
					ev.preventDefault();
				}
			}}
			onValueChange={(newValue) => {
				onChange?.(newValue);
				setInternalState(newValue);
				setIsValid(validateInput(newValue));
				setValueHasChanged((current) => current + 1);
			}}
		/>
	);
};
